import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';

interface WorkflowReevaluationModalSignature {
  Args: {
    onClose: () => void;
    onConfirm: (reason: string | null) => void | Promise<void>;
    isDenied?: boolean;
  };
}

export default class WorkflowReevaluationModal extends Component<WorkflowReevaluationModalSignature> {
  @tracked reason = '';

  @action
  handleConfirm(): void {
    const trimmedReason = this.reason.trim();
    void this.args.onConfirm(trimmedReason ? trimmedReason : null);
  }

  @action
  handleClose(): void {
    this.reason = '';
    this.args.onClose();
  }

  @action
  updateReason(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.reason = target.value;
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @translucentOverlay={{true}}
      @onClose={{this.handleClose}}
      @targetAttachment="center"
      @containerClass="ember-modal-dialog !max-w-[500px]"
      @overlayClass="ember-modal-overlay--fadeIn"
    >
      <FocusTrapper>
        <div class="p-8" data-test-workflow-reevaluation-modal>
          {{#if @isDenied}}
            <h2 class="text-2xl mb-4">Resubmit this invite for approval</h2>
            <p class="text-carbon-60 mb-6">
              Saving these changes will
              <strong>resubmit this invite for approval</strong>. The approval route may change based on the updated
              details.
            </p>
          {{else}}
            <h2 class="text-2xl mb-4">You're editing an approved record</h2>
            <p class="text-carbon-60 mb-6">
              Saving these changes will
              <strong>cancel the existing approval</strong>
              and start a new process. The approval route may change based on the updated details.
            </p>
          {{/if}}
          <div class="mb-6">
            <textarea
              id="workflow-reevaluation-reason"
              data-test-workflow-reevaluation-reason
              class="w-full p-3 border border-carbon-20 rounded resize-none"
              rows="4"
              placeholder="Enter a reason for this change (optional)"
              value={{this.reason}}
              {{on "input" this.updateReason}}
            ></textarea>
          </div>
          <ButtonGroup class="flex justify-end" @reverse={{true}}>
            <button
              type="button"
              class="btn btn--small btn--brand"
              data-test-workflow-reevaluation-confirm
              {{on "click" this.handleConfirm}}
            >
              Save and resubmit for approval
            </button>
            <button
              type="button"
              class="btn btn--small btn--outline"
              data-test-workflow-reevaluation-cancel
              {{on "click" this.handleClose}}
            >
              Cancel
            </button>
          </ButtonGroup>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
